import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uPlot from 'uplot';
import { SERIES_COLORS, THRESHOLD_COLORS, useChartTokens } from '../panels/chartPalette';
import { CHIP, CloseIcon, FOCUS, MOTION, PANEL } from '../styles';
import { seriesName } from './applyLayout';
import { ModeBand, PlotData, PlotSpec, SERIES_DRAG_TYPE, SeriesRef, TimeMarker } from './layoutTypes';
import { IconButton } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import Tooltip from '../../components/ui/Tooltip';
import Alert from '../../components/ui/Alert';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import Reveal from '../../components/ui/Reveal';
import Appear from '../../components/ui/Appear';

export interface ExplorerPlotProps {
  plot: PlotSpec;
  /** Undefined for an inline plot (it draws plot.inline) or before the first answer. */
  data: PlotData | undefined;
  window: [number, number] | null;
  onWindowChange: (w: [number, number] | null) => void;
  modeBands: ModeBand[];
  markers: TimeMarker[];
  focusTime: number | null;
  onHover: (t: number | null) => void;
  syncKey: string;
  height: number;
  editable: boolean;
  focused: boolean;
  /** The plot had series and this log has none of them. */
  missingInLog?: boolean;
  onTitleChange?: (plotId: string, title: string) => void;
  onRemoveSeries?: (plotId: string, ref: SeriesRef) => void;
  onRemovePlot?: (plotId: string) => void;
  onDropSeries?: (plotId: string, ref: SeriesRef) => void;
  onFocus?: (plotId: string) => void;
  onRetry?: () => void;
}

const FONT = '11px RobotoMono, ui-monospace, monospace';
const LABEL_FONT_PX = 10;
const EMPTY_SELECT = { left: 0, top: 0, width: 0, height: 0 };

/** "1:05", or "1:05.3" when the span is short enough for tenths to matter. */
export const fmtClock = (v: number, fine = false): string => {
  const neg = v < 0;
  const tenths = Math.round(Math.abs(v) * 10);
  const m = Math.floor(tenths / 600);
  const s = (tenths % 600) / 10;
  const ss = fine ? s.toFixed(1).padStart(4, '0') : String(Math.floor(s)).padStart(2, '0');
  return `${neg ? '-' : ''}${m}:${ss}`;
};

const fmtValue = (v: number | null | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 10000) return v.toFixed(0);
  if (a >= 100) return v.toFixed(1);
  if (a >= 1) return v.toFixed(2);
  return v.toPrecision(3);
};

export const parseDraggedRef = (raw: string): SeriesRef | null => {
  try {
    const r = JSON.parse(raw);
    if (r && typeof r.topic === 'string' && typeof r.field === 'string' && Number.isInteger(r.instance)) {
      return { topic: r.topic, instance: r.instance, field: r.field };
    }
  } catch { /* not ours */ }
  return null;
};

const hasSeriesDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes(SERIES_DRAG_TYPE);

interface Legend { key: string; label: string; color: string; ref?: SeriesRef }

const ExplorerPlot: React.FC<ExplorerPlotProps> = ({
  plot, data, window: win, onWindowChange, modeBands, markers, focusTime, onHover, syncKey,
  height, editable, focused, missingInLog, onTitleChange, onRemoveSeries, onRemovePlot,
  onDropSeries, onFocus, onRetry,
}) => {
  const tokens = useChartTokens();
  const inline = plot.inline;
  const isSpectrum = !!inline?.x_label;
  const isTime = !isSpectrum;
  const xKey = isSpectrum ? 'hz' : 'x';

  const legend: Legend[] = useMemo(() => (inline
    ? Object.keys(inline.series).map((k, i) => ({ key: k, label: k, color: SERIES_COLORS[i % SERIES_COLORS.length] }))
    : plot.series.map((s, i) => ({
      key: seriesName(s),
      label: s.label || seriesName(s),
      color: s.color || SERIES_COLORS[i % SERIES_COLORS.length],
      ref: s,
    }))), [inline, plot.series]);

  const columns = useMemo((): uPlot.AlignedData | null => {
    if (inline) return [inline.t, ...Object.values(inline.series)] as uPlot.AlignedData;
    return data?.status === 'ready' && data.columns ? (data.columns as uPlot.AlignedData) : null;
  }, [inline, data]);

  // The chart is rebuilt only when what it draws changes shape, never when its data does.
  const structureKey = JSON.stringify([
    xKey, !!inline?.bars, plot.kind || 'line', plot.y || null, legend.map(l => [l.key, l.color]),
  ]);

  const hostRef = useRef<HTMLDivElement>(null);
  const uRef = useRef<uPlot | null>(null);
  const valueEls = useRef<(HTMLSpanElement | null)[]>([]);
  const timeEl = useRef<HTMLSpanElement>(null);
  const pointerInside = useRef(false);

  // Hooks read the latest props through refs: the uPlot instance outlives many renders.
  const live = useRef({
    win, modeBands, markers, focusTime, onWindowChange, onHover,
    refLines: inline?.reference_lines, highlights: inline?.highlights, isTime,
  });
  live.current = {
    win, modeBands, markers, focusTime, onWindowChange, onHover,
    refLines: inline?.reference_lines, highlights: inline?.highlights, isTime,
  };
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const emptyData = useCallback(
    (): uPlot.AlignedData => [[], ...legend.map(() => [])] as unknown as uPlot.AlignedData,
    [legend],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const pr = uPlot.pxRatio || 1;
    const labelFont = `${LABEL_FONT_PX * pr}px RobotoMono, ui-monospace, monospace`;

    const drawUnder = (u: uPlot) => {
      const { ctx } = u;
      const { left, top, width, height: h } = u.bbox;
      const { modeBands: bands, highlights, isTime: timePlot } = live.current;
      if (!timePlot) return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, width, h);
      ctx.clip();
      ctx.font = labelFont;
      ctx.textBaseline = 'top';
      const span = (b: { start_s: number; end_s: number }) => {
        const x0 = Math.max(left, u.valToPos(b.start_s, 'x', true));
        const x1 = Math.min(left + width, u.valToPos(b.end_s, 'x', true));
        return x1 > x0 ? [x0, x1] : null;
      };
      bands.forEach((b, i) => {
        const s = span(b);
        if (!s) return;
        ctx.globalAlpha = i % 2 ? 0.1 : 0.06;
        ctx.fillStyle = tokens.axis;
        ctx.fillRect(s[0], top, s[1] - s[0], h);
        if (ctx.measureText(b.label).width + 8 * pr < s[1] - s[0]) {
          ctx.globalAlpha = 0.85;
          ctx.fillText(b.label, s[0] + 4 * pr, top + 3 * pr);
        }
      });
      (highlights || []).forEach(hl => {
        const s = span(hl);
        if (!s) return;
        const color = hl.color || THRESHOLD_COLORS.warning;
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = color;
        ctx.fillRect(s[0], top, s[1] - s[0], h);
        if (hl.label && ctx.measureText(hl.label).width + 8 * pr < s[1] - s[0]) {
          ctx.globalAlpha = 1;
          ctx.fillText(hl.label, s[0] + 4 * pr, top + 16 * pr);
        }
      });
      ctx.restore();
    };

    const drawOver = (u: uPlot) => {
      const { ctx } = u;
      const { left, top, width, height: h } = u.bbox;
      const { markers: marks, refLines, focusTime: ft, isTime: timePlot } = live.current;
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, width, h);
      ctx.clip();
      ctx.font = labelFont;
      ctx.lineWidth = pr;
      (refLines || []).forEach(r => {
        const y = u.valToPos(r.value, 'y', true);
        if (y < top || y > top + h) return;
        const color = r.color || tokens.axis;
        ctx.globalAlpha = 0.6;
        ctx.strokeStyle = color;
        ctx.setLineDash([4 * pr, 4 * pr]);
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(left + width, y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(r.label, left + width - 4 * pr, y - 2 * pr);
      });
      if (timePlot) {
        marks.forEach(m => {
          const x = u.valToPos(m.t, 'x', true);
          if (x < left || x > left + width) return;
          ctx.globalAlpha = 0.7;
          ctx.strokeStyle = m.color;
          ctx.setLineDash([3 * pr, 3 * pr]);
          ctx.beginPath();
          ctx.moveTo(x, top);
          ctx.lineTo(x, top + h);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.fillStyle = m.color;
          ctx.textBaseline = 'top';
          const room = left + width - x;
          const w = ctx.measureText(m.label).width;
          ctx.textAlign = room > w + 8 * pr ? 'left' : 'right';
          ctx.fillText(m.label, ctx.textAlign === 'left' ? x + 4 * pr : x - 4 * pr, top + 16 * pr);
        });
        // The page's cursor, drawn only when no explorer plot owns the pointer.
        const synced = u.cursor.left != null && u.cursor.left >= 0;
        if (ft != null && !pointerInside.current && !synced) {
          const x = u.valToPos(ft, 'x', true);
          if (x >= left && x <= left + width) {
            ctx.globalAlpha = 1;
            ctx.setLineDash([]);
            ctx.strokeStyle = tokens.primary;
            ctx.beginPath();
            ctx.moveTo(x, top);
            ctx.lineTo(x, top + h);
            ctx.stroke();
          }
        }
      }
      ctx.restore();
    };

    const readout = (u: uPlot) => {
      const idx = u.cursor.idx;
      const xs = u.data[0];
      if (timeEl.current) {
        const t = idx != null ? xs[idx] : null;
        const sc = u.scales[xKey];
        const fine = sc.min != null && sc.max != null && sc.max - sc.min < 60;
        timeEl.current.textContent = t == null ? '' : (isSpectrum ? `${fmtValue(t)} Hz` : fmtClock(t, fine));
      }
      valueEls.current.forEach((el, i) => {
        if (!el) return;
        const col = u.data[i + 1];
        const j = u.cursor.idxs?.[i + 1] ?? idx; // a joined column is undefined at the other topic's times
        el.textContent = j != null && col ? fmtValue(col[j] as number | null | undefined) : '';
      });
      if (pointerInside.current && live.current.isTime && u.cursor.left != null && u.cursor.left >= 0) {
        live.current.onHover(u.posToVal(u.cursor.left, 'x'));
      }
    };

    const opts: uPlot.Options = {
      width: Math.max(host.clientWidth, 100),
      height,
      padding: [8, 10, 0, 0],
      legend: { show: false },
      scales: {
        [xKey]: {
          time: false,
          range: (_u, min, max) => {
            const w = live.current.win;
            if (isTime && w) return [w[0], w[1]];
            if (min == null || max == null) return [0, 1];
            return max > min ? [min, max] : [min - 0.5, max + 0.5];
          },
        },
        y: plot.y && plot.y.min != null && plot.y.max != null
          ? { range: [plot.y.min, plot.y.max] }
          : {},
      },
      axes: [
        {
          scale: xKey,
          stroke: tokens.axis,
          font: FONT,
          size: 26,
          grid: { stroke: tokens.grid, width: 1, dash: [3, 3] },
          ticks: { stroke: tokens.grid, width: 1, size: 4 },
          values: isSpectrum
            ? undefined
            : (u, splits) => {
              const sc = u.scales.x;
              const fine = sc.min != null && sc.max != null && sc.max - sc.min < 60;
              return splits.map(v => fmtClock(v, fine));
            },
        },
        {
          stroke: tokens.axis,
          font: FONT,
          grid: { stroke: tokens.grid, width: 1, dash: [3, 3] },
          ticks: { stroke: tokens.grid, width: 1, size: 4 },
          size: (_u, values) => {
            const longest = (values || []).reduce((n, v) => Math.max(n, String(v).length), 0);
            return Math.max(34, longest * 7 + 14);
          },
        },
      ],
      series: [
        { scale: xKey },
        ...legend.map((l): uPlot.Series => {
          const s: uPlot.Series = {
            label: l.label,
            stroke: l.color,
            width: 1.25,
            points: { show: false },
          };
          if (inline?.bars) {
            s.paths = uPlot.paths.bars!({ size: [0.8, 48] });
            s.fill = l.color;
            s.alpha = 0.85;
          } else if (plot.kind === 'step') {
            s.paths = uPlot.paths.stepped!({ align: 1 });
          }
          return s;
        }),
      ],
      cursor: {
        y: false,
        points: { size: 5 },
        drag: isTime ? { x: true, y: false, setScale: false, dist: 6 } : { x: false, y: false, setScale: false },
        sync: isTime ? { key: syncKey, setSeries: false, scales: ['x', null] } : undefined,
        // Double-click is ours: it clears the shared window rather than this chart's scale.
        bind: { dblclick: () => null },
      },
      hooks: {
        drawClear: [drawUnder],
        draw: [drawOver],
        setCursor: [readout],
        setSelect: [u => {
          if (!live.current.isTime) return;
          const { left, width } = u.select;
          if (width > 4) {
            const a = u.posToVal(left, 'x');
            const b = u.posToVal(left + width, 'x');
            if (b - a > 0.05) live.current.onWindowChange([a, b]);
          }
          u.setSelect(EMPTY_SELECT, false);
        }],
      },
    };

    const u = new uPlot(opts, columnsRef.current || emptyData(), host);
    uRef.current = u;

    const sel = u.root.querySelector('.u-select') as HTMLElement | null;
    if (sel) { sel.style.background = tokens.primary; sel.style.opacity = '0.15'; }
    const cx = u.root.querySelector('.u-cursor-x') as HTMLElement | null;
    if (cx) { cx.style.borderRightColor = tokens.primary; cx.style.borderRightStyle = 'solid'; }

    const enter = () => { pointerInside.current = true; };
    const leave = () => {
      pointerInside.current = false;
      if (live.current.isTime) live.current.onHover(null);
    };
    const dbl = () => { if (live.current.isTime) live.current.onWindowChange(null); };
    u.over.addEventListener('mouseenter', enter);
    u.over.addEventListener('mouseleave', leave);
    u.over.addEventListener('dblclick', dbl);

    return () => {
      u.over.removeEventListener('mouseenter', enter);
      u.over.removeEventListener('mouseleave', leave);
      u.over.removeEventListener('dblclick', dbl);
      if (pointerInside.current) live.current.onHover(null);
      pointerInside.current = false;
      u.destroy();
      uRef.current = null;
    };
    // Rebuilt on structure only; data, window, overlays and size update the live instance.
  }, [structureKey, syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    uRef.current?.setData(columns || emptyData());
  }, [columns, structureKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const u = uRef.current;
    if (!u || !isTime) return;
    const xs = u.data[0];
    if (win) u.setScale('x', { min: win[0], max: win[1] });
    else if (xs.length) u.setScale('x', { min: xs[0], max: xs[xs.length - 1] });
  }, [win, isTime, structureKey]);

  useEffect(() => {
    uRef.current?.redraw(false, false);
  }, [modeBands, markers, focusTime, inline]);

  useEffect(() => {
    const u = uRef.current;
    const host = hostRef.current;
    if (u && host) u.setSize({ width: Math.max(host.clientWidth, 100), height });
  }, [height, structureKey]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    let last = host.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = host.clientWidth;
      if (w === last || w < 1) return;
      last = w;
      uRef.current?.setSize({ width: Math.max(w, 100), height });
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [height]);

  /* ── Header: title, legend chips, actions ── */

  const fallbackTitle = plot.series[0]?.topic || (inline ? 'Chart' : 'New plot');
  const title = plot.title || fallbackTitle;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== plot.title) onTitleChange?.(plot.id, next);
  };

  const [dragOver, setDragOver] = useState(false);
  const canDrop = editable && !!onDropSeries && plot.series.length < 16;

  const focus = useCallback(() => onFocus?.(plot.id), [onFocus, plot.id]);

  const status = inline ? 'ready' : plot.series.length === 0 ? 'empty' : (data?.status || 'loading');
  const noRows = status === 'ready' && columns != null && (columns[0] as number[]).length === 0;

  return (
    <section
      aria-label={title}
      className={`${PANEL} relative ${MOTION} ${focused && editable ? 'ring-1 ring-primary/50' : ''} ${dragOver ? 'bg-surface-container' : ''}`}
      onPointerDownCapture={focus}
      onFocusCapture={focus}
      onDragOver={canDrop ? (e) => {
        if (!hasSeriesDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        if (!dragOver) setDragOver(true);
      } : undefined}
      onDragLeave={canDrop ? (e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      } : undefined}
      onDrop={canDrop ? (e) => {
        setDragOver(false);
        const ref = parseDraggedRef(e.dataTransfer.getData(SERIES_DRAG_TYPE));
        if (!ref) return;
        e.preventDefault();
        onDropSeries!(plot.id, ref);
      } : undefined}
    >
      <div className="flex min-w-0 flex-col gap-1.5 bg-surface-container px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {editing ? (
            <Field
              variant="inline"
              autoFocus
              value={draft}
              maxLength={80}
              aria-label="Plot title"
              onChange={e => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={e => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') { setEditing(false); setDraft(title); }
              }}
              className="min-w-0 flex-1 font-semibold"
            />
          ) : editable && onTitleChange ? (
            <Tooltip label={`${title}. Click to rename.`}>
            <button
              type="button"
              aria-label={`Rename ${title}`}
              onClick={() => { setDraft(title); setEditing(true); }}
              className={`np-state -mx-1.5 min-w-0 truncate rounded px-1.5 py-0.5 text-left text-sm font-semibold ${plot.title ? 'text-on-surface' : 'text-on-surface-variant'} ${FOCUS}`}
            >
              {title}
            </button>
            </Tooltip>
          ) : (
            <h3 className="min-w-0 truncate text-sm font-semibold text-on-surface" title={title}>{title}</h3>
          )}
          {inline?.y_label && (
            <span className="shrink-0 whitespace-nowrap text-xs text-on-surface-variant">{inline.y_label}</span>
          )}
          <Appear show={!!data?.bucketed} className="shrink-0 whitespace-nowrap text-xs text-on-surface-variant">
            <span title="More rows than pixels: each step shows the low and the high of its slice. Zoom in for every row.">
              summarized
            </span>
          </Appear>
          <span ref={timeEl} aria-hidden="true" className="ml-auto shrink-0 whitespace-nowrap font-mono text-xs tabular-nums text-on-surface" />
          {editable && onRemovePlot && (
            <Tooltip label="Remove this plot from the tab">
              <IconButton
                label={`Remove plot ${title}`}
                size="sm"
                onClick={() => onRemovePlot(plot.id)}
                className="-my-1 -mr-1.5"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <Reveal show={legend.length > 0}>
          <ul className="flex min-w-0 flex-wrap gap-1" aria-label="Series">
            {legend.map((l, i) => (
              <li
                key={l.key}
                className={`${CHIP} min-w-0 max-w-full bg-surface-container-high text-on-surface-variant ${editable && l.ref && onRemoveSeries ? 'pr-0.5' : ''}`}
              >
                <span aria-hidden="true" className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: l.color }} />
                <span className="min-w-0 truncate" title={l.label}>{l.label}</span>
                <span
                  ref={el => { valueEls.current[i] = el; }}
                  aria-hidden="true"
                  className="shrink-0 font-mono tabular-nums text-on-surface empty:hidden"
                />
                {editable && l.ref && onRemoveSeries && (
                  <button
                    type="button"
                    aria-label={`Remove ${l.label}`}
                    onClick={() => onRemoveSeries(plot.id, l.ref!)}
                    className={`np-state inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-on-surface-variant hover:text-on-surface ${FOCUS} ${MOTION} [&_svg]:h-3 [&_svg]:w-3`}
                  >
                    <CloseIcon />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <div className="relative min-w-0 px-1 pb-1 pt-1" style={{ height: height + 8 }}>
        <div ref={hostRef} className="min-w-0" />
        <Appear as="div" show={status === 'loading'} className="!scale-100 absolute inset-1 rounded bg-surface-container-low p-3">
          <div className="h-full w-full" aria-label="Loading the plot">
            <Skeleton className="h-full w-full rounded" />
          </div>
        </Appear>
        <Appear as="div" show={status === 'error'} className="!scale-100 absolute inset-1 flex items-center justify-center rounded bg-surface-container-low px-4">
          <Alert variant="note" onRetry={onRetry} retryLabel="Retry" retryTip="Load this plot again">
            {data?.error || 'This plot did not load.'}
          </Alert>
        </Appear>
        <Appear as="div" show={(status === 'empty' || noRows || (inline && legend.length === 0)) && !dragOver} className="!scale-100 absolute inset-1 flex items-center justify-center rounded bg-surface-container-low">
            <EmptyState variant="area">
              {noRows ? 'No rows in this window.'
                : inline ? 'This chart has no data.'
                  : missingInLog ? 'No series in this log.'
                    : editable ? 'Drop a field here.' : 'No series in this log.'}
              {status === 'empty' && editable && !missingInLog && (
                <span className="mt-1 block text-xs">Or click one in Fields while this plot is selected.</span>
              )}
            </EmptyState>
        </Appear>
        <Appear as="div" show={!!dragOver} className="pointer-events-none absolute inset-1 flex items-center justify-center rounded bg-primary/10">
          <span className="rounded-full bg-surface-container-highest px-3 py-1 text-sm font-medium text-primary">
            Drop to add to {title}
          </span>
        </Appear>
      </div>
    </section>
  );
};

export default React.memo(ExplorerPlot);
